/**
 * The per-CODE rows under an expanded site row — CR-7288's third tier.
 *
 * Sarah's ask, 14 Sep: supplier → its sites → all codes (N) → per-code split.
 * A site row covers every code the relationship holds; these split it.
 */
import type { ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { SupplierRatingThreshold, OTIFGradeThreshold, SupplierCustomerSite } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { PerformanceTableRow, RowCallbacks } from './PerformanceTableRow';
import { useCodeBreakdown, BreakdownCode } from './useCodeBreakdown';
import { hasNoScopedActivity } from './utils';

interface CodeBreakdownRowsProps extends RowCallbacks {
  supplierId: number;
  site: SupplierCustomerSite;
  thresholds: SupplierRatingThreshold[];
  /** CR-6269: OTIF % colour bands, threaded to every row. */
  otifBands?: OTIFGradeThreshold[];
  currency: string;
  columnCount: number;
  buildCodeRowParams: (siteId: number, supplierId: number, code: BreakdownCode) => Record<string, string | number | boolean>;
}

export function CodeBreakdownRows({
  supplierId,
  site,
  thresholds,
  otifBands = [],
  currency,
  columnCount,
  buildCodeRowParams,
  onOpenGRNModal,
  onOpenNCRModal,
  onOpenPOLineModal,
}: CodeBreakdownRowsProps) {
  const codes = site.supplier_codes ?? [];
  // The bucket is asked for ALWAYS, and rendered only if it comes back with
  // something. Deciding up front would need a count we do not have, and a
  // silently dropped remainder is exactly the failure the tier exists to stop.
  const { data, loading, error, refetch } = useCodeBreakdown(
    supplierId, site.id, codes, true, buildCodeRowParams, true,
  );

  const message = (content: ReactNode, key: string) => (
    <tr key={key} className="border-b bg-slate-100 dark:bg-slate-950">
      <td colSpan={columnCount} className="p-3 pl-20 text-xs text-muted-foreground">
        <div className="sticky left-20 inline-flex items-center gap-2">{content}</div>
      </td>
    </tr>
  );

  if (loading) {
    return message(
      <>
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        Loading {site.name} by code…
      </>,
      'loading',
    );
  }
  if (error) {
    return message(
      <>
        <span className="text-red-600">Could not load the per-code figures: {error}</span>
        <Button variant="outline" size="sm" className="h-6 px-2 text-xs" onClick={() => refetch()}>
          Retry
        </Button>
      </>,
      'error',
    );
  }

  const rows = (data ?? []).filter(({ code, profile }) => {
    if (!profile) return false;
    // An issued code with no work against it still gets a row — that IS the
    // answer to "what happened on this account". The (no code) bucket does
    // not: it is an artefact of incomplete data, so it appears only when it
    // holds something, and then it is what makes the rows sum to the site.
    if (code === null) {
      // Round 1 hid the bucket on `po_line_count` alone — the OTIF engine's
      // closed, judged lines — and dropped a row whose only activity was
      // receipts or NCRs. Round 2 found the four-field replacement still blind
      // to arrears, value and the reject figures. So the test is now the
      // table's whole scoped column set (`SCOPED_ACTIVITY_FIELDS`), and a new
      // column joins that list rather than being forgotten here.
      return !hasNoScopedActivity(profile as unknown as Record<string, unknown>);
    }
    return true;
  });

  if (rows.length === 0) {
    return message(`No code-level figures for ${site.name} in this period`, 'empty');
  }

  return (
    <>
      {rows.map(({ code, profile }) => (
        <PerformanceTableRow
          key={`code-${supplierId}-${site.id}-${code ?? 'none'}`}
          supplier={profile!}
          thresholds={thresholds}
          otifBands={otifBands}
          currency={currency}
          viewerIsSupplier={false}
          showSupplierSite={false}
          variant="code"
          site={{ ...site, supplier_codes: code === null ? [] : [code] }}
          // CR-7288. Aaron's instruction was "just show Supplier Code: XXXX";
          // shortening it to "Code X" was MY recommendation and his answer was
          // "agreed on your rec" — recorded that way round because the reason
          // is mine, not his. The reason: "Supplier Code: V000483" wrapped onto
          // two lines and was the only cell in the column that broke, the
          // column header already says Supplier, and this matches the site rows
          // above so only the indent tells you which level you are on.
          codeLabelOverride={code === null ? 'No code recorded' : `Code ${code}`}
          drillCode={code ?? undefined}
          drillNoCode={code === null}
          onOpenGRNModal={onOpenGRNModal}
          onOpenNCRModal={onOpenNCRModal}
          onOpenPOLineModal={onOpenPOLineModal}
        />
      ))}
    </>
  );
}
